<template>
  <BaseLayout title="Edit Homework">
    <!-- Main Content (Center) -->
    <div class="container my-4">
      <h1 class="display-4 text-center">Edit Homework</h1>
      <div class="card">
        <div class="card-body">
          <HomeworkForm
            :homework="homework"
            :teachers="teachers"
            :semesters="semesters"
            buttonText="Save"
            @submitForm="updateHomework"
          />
        </div>
      </div>
      <div class="card mt-4">
        <div class="card-body">
          <h5 class="card-title">Description History</h5>
          <p class="text-muted small">View how the homework description changed over time.</p>
          <button class="btn btn-outline-info btn-sm" @click="openDescriptionDiff">
            <i class="bi bi-file-diff"></i> View Description Diff
          </button>
        </div>
      </div>

      <div class="card mt-4">
        <div class="card-body">
          <h5 class="card-title">Deadlines</h5>
          <div v-for="deadline in deadlines" :key="deadline.id" class="mb-3">
            <DeadlineForm
              :deadline="deadline"
              buttonText="Update Deadline"
              @submitForm="updateDeadline"
              @deleteDeadline="removeDeadline"
            />
          </div>
          <button @click="deadlineModalToggle" class="btn btn-info">
            {{ isDeadlineModalVisible ? 'Close' : 'Add Deadline' }}
          </button>
          <DeadlineForm
            v-if="isDeadlineModalVisible"
            :deadline="newDeadline"
            buttonText="Add Deadline"
            @submitForm="addDeadline"
          />
        </div>
      </div>
    </div>

    <!-- Right Sidebar Slot -->
    <template v-slot:right-sidebar>
      <div class="card p-3 mb-3">
        <h5 class="card-title">Add:</h5>
        <router-link :to="`/subtask/create/${homework.id}`" class="btn btn-info mb-2">
          <i class="bi bi-plus-circle"></i> New Task
        </router-link>
      </div>
      <!-- Routers to homework subtasks -->
      <div v-if="homework.subtasks && homework.subtasks.length" class="card p-3 mb-3"> 
        <h5 class="card-title">Tasks:</h5>
        <router-link v-for="subtask in sortedSubtasks" :key="subtask.id" :to="`/subtask/${subtask.id}`" class="btn btn-primary mb-2">
          <i class="bi bi-arrow-right-circle"></i> {{ subtask.title }}
        </router-link>
      </div>
    </template>

    <DiffModal ref="diffModal" @error="(message) => $refs.stemErrorModal.show('Error', message)" />

    <StemErrorModal ref="stemErrorModal"/>
  </BaseLayout>
</template>

<script>
import BaseLayout from '@/components/BaseLayout.vue';
import HomeworkForm from '@/components/HomeworkForm.vue';
import DeadlineForm from '@/components/DeadlineForm.vue';
import HomeworkController from '@/controllers/HomeworkController';
import SubtaskController from '@/controllers/SubtaskController';
import DeadlineController from '@/controllers/DeadlineController';
import StemErrorModal from '@/components/StemErrorModal.vue';
import Homework from '@/models/homework';
import Subtask from '@/models/subtask';
import Deadline from '@/models/deadline';
import { mapGetters } from 'vuex';
import DiffModal from '@/components/DiffModal.vue';
import { savedVersionDiff } from '@/utils/diff';

export default {
  name: 'HomeworkEditView',
  components: {
    BaseLayout,
    HomeworkForm,
    DeadlineForm,
    DiffModal,
    StemErrorModal,
  },
  computed: {
    ...mapGetters(['isTeacher',  'userId', 'semesterId']),
    sortedSubtasks() {
      // Return sorted subtasks based on subtask.number in ascending order
      return (this.homework.subtasks || []).slice().sort((a, b) => a.number - b.number);
    },
  },
  data() {
    return {
      homework: new Homework({}),
      teachers: [],
      semesters: [],
      subtasks: [],
      deadlines: [],
      newSubtask: new Subtask({ homework_id: this.$route.params.id }),
      newDeadline: new Deadline({ homework_id: this.$route.params.id }),
      isSubtaskModalVisible: false,
      isDeadlineModalVisible: false,
    };
  },
  async created() {
    try {
      // Check if the user is a teacher
      if (!this.isTeacher) {
        this.$store.dispatch('triggerAlert', {
          type: 'error',
          title: 'Error',
          message: 'You are not authorized to access this page.',
        });
        this.$router.push('/homeworks');
        return;
      }

      const homeworkId = this.$route.params.id;
      this.homework = await HomeworkController.fetchHomeworkByIdWithRelatedData(homeworkId);                
      if (this.homework.subtasks && this.homework.subtasks.length > 0) {
        this.subtasks = this.homework.subtasks;
      }
      if (this.homework.deadlines && this.homework.deadlines.length > 0) {
        this.deadlines = this.homework.deadlines;
      }

      // Set teacher and semester
      this.homework.teacher_id = this.userId;
      this.homework.semester_id = this.semesterId;
    } catch (error) {
      this.$refs.stemErrorModal.show('Error', error.message);
    }
  },
  methods: {
    async updateHomework(updatedHomework) {
      try {
        await HomeworkController.updateHomework(this.homework.id, updatedHomework);

        // Upload first — same-name edits overwrite in place, so delete is not needed.
        // Delete runs after so a failed upload still leaves the original on disk.
        await this.updateFiles(this.homework.id, updatedHomework);
        await this.deleteFiles(this.homework.id, updatedHomework.filesToDelete);

        this.$store.dispatch('triggerAlert', {
          title: 'Success',
          message: 'Homework was updated successfully',
          type: 'success',
        });
        this.$router.push(`/homework/${this.homework.id}`);
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      }
    },
    async deleteHomework() {
      if (confirm('Are you sure you want to delete this homework?')) {
        try {
          await HomeworkController.deleteHomework(this.homework.id);
          this.$store.dispatch('triggerAlert', {
            title: 'Success',
            message: 'Homework was deleted successfully',
            type: 'success',
          });
          this.$router.push('/homeworks');
        } catch (error) {
          this.$refs.stemErrorModal.show('Error', error.message);
        }
      }
    },
    async updateFiles(homework_id, updatedHomework) {
      if (updatedHomework.homework_files.length > 0) {
        const formData = new FormData();
        for (const file of updatedHomework.homework_files) {
          formData.append('files', file);
        }
        await HomeworkController.uploadFiles(homework_id, formData);
      }
    },
    async deleteFiles(homework_id, filesToDelete) {
      for (const filePath of filesToDelete.file_paths) {
        await HomeworkController.deleteFile(homework_id, filePath);
      }
    },
    async subtaskModalToggle() {
      this.isSubtaskModalVisible = !this.isSubtaskModalVisible;
    },
    async deadlineModalToggle() {
      this.isDeadlineModalVisible = !this.isDeadlineModalVisible;
    },
    openDescriptionDiff() {
      this.$refs.diffModal.open('Description — Diff with Previous', async () =>
        savedVersionDiff(await HomeworkController.getDescriptionDiff(this.homework.id)));
    },
    async addSubtask(subtaskData) {
      try {
        const newSubtask = await SubtaskController.createSubtask(subtaskData);
        this.subtasks.push(newSubtask);

        // Upload files after subtask is created
        await this.uploadSubtaskFiles(newSubtask.id, subtaskData);

        this.$store.dispatch('triggerAlert', {
          title: 'Success',
          message: 'Task was created successfully',
          type: 'success',
        });
        // Reset new subtask = prepare for next subtask
        this.newSubtask = new Subtask({ homework_id: this.homework.id });
        this.isSubtaskModalVisible = false;
      } catch (error) {        
        this.$refs.stemErrorModal.show('Error', error.message);
      }
    },
    async uploadSubtaskFiles(subtaskId, subtaskData) {
      const { resource_files, submission_files, test_files } = subtaskData;

      if (resource_files.length > 0) {
        const formData = new FormData();
        for (const file of resource_files) {
          formData.append('files', file);
        }
        await SubtaskController.uploadFiles('save_subtask_resources', subtaskId, this.homework.id, formData);
      }

      if (submission_files.length > 0) {
        const formData = new FormData();
        for (const file of submission_files) {
          formData.append('files', file);
        }
        await SubtaskController.uploadFiles('save_subtask_submissions', subtaskId, this.homework.id, formData);
      }

      if (test_files.length > 0) {
        const formData = new FormData();
        for (const file of test_files) {
          formData.append('files', file);
        }
        await SubtaskController.uploadFiles('save_subtask_tests', subtaskId, this.homework.id, formData);
      }
    },
    async addDeadline(deadlineData) {
      try {
        const newDeadline = await DeadlineController.createDeadline(deadlineData);
        this.$store.dispatch('triggerAlert', {
          title: 'Success',
          message: 'Deadline was created successfully',
          type: 'success',
        });
        this.deadlines.push(newDeadline);

        // Reset new deadline = prepare for next deadline
        this.newDeadline = new Deadline({ homework_id: this.homework.id });
        this.isDeadlineModalVisible = false;
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      }
    },
    async updateDeadline(deadlineData) {
      try {
        await DeadlineController.updateDeadline(deadlineData.id, deadlineData);
        this.$store.dispatch('triggerAlert', {
          title: 'Success', message: 'Deadline was updated successfully', type: 'success'
        });
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      }
    },
    async removeDeadline(deadlineId) {
      this.deadlines = this.deadlines.filter(deadline => deadline.id !== deadlineId);
    },
  },
};
</script>